import { Link } from '@tanstack/react-router';
import { PLANS } from '@aeon-karaoke/cloud-convex/model/plans';
import { useBillingActions, useBillingSummary, type BillingSummary, type CheckoutVariant } from '@/hook';
import { formatPrice } from '@/lib/money';
import { formatRenewalDate } from '@/lib/time';
import { Button } from './Button';
import { LoadingState } from './LoadingState';
import { Modal } from './Modal';
import { MonoLabel } from './MonoLabel';

interface PlanModalProps {
  onClose: () => void;
  onCheckout: (variant: CheckoutVariant) => void;
}

export interface PlanModalCopy {
  price: string;
  priceNote: string;
  usageHeading: string;
  usage: Array<[string, string]>;
  primary: { label: string; action: 'beta' | 'keep' } | null;
}

/** The plan modal's words from the summary, so the free and subscribed
 * contents are checked side by side in PlanModal.spec.tsx. */
export function planModalCopy(summary: BillingSummary): PlanModalCopy {
  const open = summary.checkout.open;
  const free = summary.tier === 'free';
  const { used, limit } = summary.usage;
  const { inPlan, perPlan } = summary.packs;
  const note = free
    ? summary.paymentMethod
      ? 'card on file'
      : 'no card on file'
    : [
        `per ${summary.interval === 'year' ? 'year' : 'month'}`,
        summary.endsAt
          ? `ends ${formatRenewalDate(summary.endsAt)}`
          : summary.pendingPlan
            ? `${PLANS[summary.pendingPlan.tier].name} from ${formatRenewalDate(summary.pendingPlan.startsAt)}`
            : summary.renewsAt
              ? `renews ${formatRenewalDate(summary.renewsAt)}`
              : null,
      ]
        .filter(Boolean)
        .join(' · ');
  return {
    price: free ? '$0' : formatPrice(summary.priceCents),
    priceNote: note,
    usageHeading: free ? 'What you have' : 'This month',
    usage: [
      ['Tracks created', free ? `${used} of ${limit} this month` : `${used} of ${limit ?? 'unlimited'}`],
      ['Song packs', free && inPlan === perPlan.free ? `${perPlan.free} starter` : String(inPlan)],
    ],
    // With checkout closed the upgrade goes and Keep stays. A subscriber on a
    // plan no longer sold is offered nothing: Beta is a Checkout, not a switch.
    primary: free
      ? open
        ? { label: `Upgrade to ${PLANS.beta.name}`, action: 'beta' }
        : null
      : summary.endsAt || summary.pendingPlan
        ? { label: `Keep ${summary.plan.name}`, action: 'keep' }
        : null,
  };
}

/** Current plan, price, renewal, usage this month, the primary action and
 * the payment, receipts and cancel links. The contents switch entirely
 * between a free account and a subscriber. */
export function PlanModal({ onClose, onCheckout }: PlanModalProps) {
  const summary = useBillingSummary();
  const { keep, openPortal, pending, error } = useBillingActions();

  if (!summary) {
    return (
      <Modal open onClose={onClose} title="Your plan">
        <LoadingState />
      </Modal>
    );
  }
  const copy = planModalCopy(summary);
  const free = summary.tier === 'free';

  function primary() {
    if (!copy.primary) return;
    if (copy.primary.action === 'keep') void keep();
    else onCheckout({ kind: copy.primary.action });
  }

  return (
    <Modal open onClose={onClose} title={summary.plan.name} kicker="Your plan">
      <div className="mt-5 flex items-baseline gap-2">
        <span className="text-[38px] font-semibold tracking-[-0.035em]">{copy.price}</span>
        <span className="text-sm text-mute">{copy.priceNote}</span>
      </div>
      <div className="mt-[26px]">
        <MonoLabel size="md" className="block border-b border-line pb-[10px]">
          {copy.usageHeading}
        </MonoLabel>
        {copy.usage.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 border-b border-stripeA py-[13px]">
            <span className="text-sm text-mute">{k}</span>
            <span className="text-sm font-semibold">{v}</span>
          </div>
        ))}
      </div>
      <div className="mt-7 flex flex-wrap gap-[10px]">
        {copy.primary && (
          <Button disabled={pending} onClick={primary}>
            {copy.primary.label}
          </Button>
        )}
        <Link to="/settings/$tab" params={{ tab: 'plan' }} onClick={onClose}>
          <Button variant="outline" tabIndex={-1}>
            Compare plans
          </Button>
        </Link>
      </div>
      {error && <p className="mt-3 text-[13px] text-down">{error}</p>}
      <div className="mt-[22px] flex flex-wrap gap-5 border-t border-line pt-[18px] text-[13px] text-mute">
        <button type="button" className="hover:text-ink" onClick={() => void openPortal()}>
          Update payment method
        </button>
        <Link to="/settings/$tab" params={{ tab: 'plan' }} hash="receipts" onClick={onClose} className="hover:text-ink">
          Receipts
        </Link>
        {!free && !summary.endsAt && (
          <button type="button" className="hover:text-ink" onClick={() => onCheckout({ kind: 'cancel' })}>
            Cancel subscription
          </button>
        )}
      </div>
    </Modal>
  );
}
